import React, { useState, useEffect, useRef } from "react";
import {
  EditorState,
  Modifier,
  RichUtils,
  ContentState,
  convertToRaw,
  convertFromRaw,
} from "draft-js";
import { convertToHTML } from "draft-convert";
import { Editor } from "react-draft-wysiwyg";
import DOMPurify from "dompurify";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Toolbar from "./ToolBar";
import CustomToolbar from "./ToolBar";
import { toast } from "react-toastify";
import htmlToDraft from "html-to-draftjs";
import { useLocalDraftJson } from "../../hooks/useLocalDraft";
import {
  preserveBlankLines,
  preserveExtraSpaces,
} from "../../Utils/preserveEditorWhitespace";

const MessageBox = ({
  onSendClick,
  isExpandedByDefault,
  initialText = "",
  isEditing = false,
  onCancelEdit, // Add this prop
  setEditingMessage,
  isMessageBoxDisabled = false,
  /** Optional: enables localStorage drafts per user + scope (e.g. journal id or chat id) */
  userProfile,
  conversationId,
  /** When set, used as the draft scope instead of conversationId (e.g. `${chatId}:${messageId}`) */
  draftScopeId,
}) => {
  const draftScope = draftScopeId ?? conversationId;
  const messageDraftEnabled = Boolean(
    userProfile?.id && draftScope && !isEditing,
  );

  const {
    value: draftRawContent,
    setValue: persistMessageDraft,
    clearDraft: clearMessageDraft,
  } = useLocalDraftJson({
    userId: userProfile?.id,
    scopeId: draftScope,
    fieldKey: "chat.input.message",
    enabled: messageDraftEnabled,
    initialValue: null,
    syncValueToState: false,
  });

  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault || false);
  const [imageUrl, setImageUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  const textInputRef = useRef(null);
  const editorRef = useRef(null); // Add editor ref
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const [showFooter, setShowFooter] = useState(false);

  const decodeEntitiesRecursively = (text = "", maxIterations = 3) => {
    if (!text) return "";
    let decoded = text;
    let previous = text;
    let iterations = 0;

    // We want to decode entities like &amp;#x27; -> '
    // BUT we must preserve HTML tags.
    // Using DOMParser.parseFromString and then reading innerHTML
    // will decode entities while keeping the tag structure.
    do {
      previous = decoded;
      const doc = new DOMParser().parseFromString(decoded, "text/html");
      decoded = doc.body.innerHTML || "";
      iterations += 1;
    } while (decoded !== previous && iterations < maxIterations);

    return decoded;
  };

  const handleTextFocus = () => {
    setTextInput("");
    setIsExpanded(true);
    // Focus the editor after expansion
    setTimeout(() => {
      if (editorRef.current) {
        if (editorRef.current.focusEditor) {
          editorRef.current.focusEditor();
        } else {
          const editorElement = document.querySelector(".rdw-editor-main");
          if (editorElement) {
            editorElement.focus();
          }
        }
      }
    }, 100);
  };

  const handleCloseEditor = () => {
    if (isEditing && onCancelEdit) {
      onCancelEdit();
    } else {
      const newEditorState = EditorState.createEmpty();
      setEditorState(newEditorState);
      setIsExpanded(false);
    }
  };

  const messageBoxHeight = isExpandedByDefault
    ? "250px"
    : isExpanded
      ? "550px"
      : "auto";

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        setImageUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const recorder = new MediaRecorder(stream);

      recorder.ondataavailable = (event) => {
        setAudioChunks((prevChunks) => [...prevChunks, event.data]);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
        setAudioChunks([]);
        setIsRecording(false);
      };

      recorder.start();
      setIsRecording(true);

      const icon = document.getElementById("audioIcon");
      if (icon) {
        icon.style.filter =
          "invert(21%) sepia(100%) saturate(7414%) hue-rotate(359deg) brightness(94%) contrast(117%)";
      }

      setMediaRecorder(recorder);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      alert("Unable to access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      const tracks = mediaRecorder.stream.getTracks();
      tracks.forEach((track) => track.stop());
      setMediaRecorder(null);
    }
  };

  const handleVoiceRecording = () => {
    if (!isRecording) {
      startRecording();
    } else {
      stopRecording();
      const icon = document.getElementById("audioIcon");
      if (icon) {
        icon.style.filter = "none";
      }
    }
  };

  const handleTextChange = (e) => {
    setTextInput(e.target.value);
  };

  const handleEditorStateChange = (newEditorState) => {
    try {
      if (newEditorState && newEditorState.getCurrentContent) {
        setEditorState(newEditorState);
        if (messageDraftEnabled) {
          const content = newEditorState.getCurrentContent();
          if (content.hasText()) {
            persistMessageDraft(convertToRaw(content));
          } else {
            clearMessageDraft();
          }
        }
      }
    } catch (error) {
      console.warn("Editor state error, resetting:", error);
      setEditorState(EditorState.createEmpty());
    }
  };

  useEffect(() => {
    if (isEditing && initialText) {
      try {
        // Normalize legacy stored content like "&amp;#x27;" before parsing.
        const normalizedHtml = decodeEntitiesRecursively(initialText);

        const safeHtml = DOMPurify.sanitize(normalizedHtml);

        const parsed = htmlToDraft(safeHtml);

        const contentState =
          parsed?.contentBlocks?.length > 0
            ? ContentState.createFromBlockArray(
                parsed.contentBlocks,
                parsed.entityMap,
              )
            : ContentState.createFromText(
                normalizedHtml.replace(/<[^>]*>/g, "").trim(),
              );

        const newEditorState = EditorState.createWithContent(contentState);
        setEditorState(newEditorState);
        setIsExpanded(true);
      } catch (error) {
        console.error("DEBUG: Error setting initial text:", error);
        setEditorState(EditorState.createEmpty());
      }
    } else if (!messageDraftEnabled) {
      setEditorState(EditorState.createEmpty());
      setIsExpanded(false);
    } else if (draftRawContent) {
      try {
        setEditorState(
          EditorState.createWithContent(convertFromRaw(draftRawContent)),
        );
      } catch {
        setEditorState(EditorState.createEmpty());
      }
      setIsExpanded(isExpandedByDefault || false);
    } else {
      setEditorState(EditorState.createEmpty());
      setIsExpanded(isExpandedByDefault || false);
    }
  }, [
    initialText,
    isEditing,
    draftRawContent,
    messageDraftEnabled,
    isExpandedByDefault,
  ]);

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleEmojiSelect = (event) => {
    event.stopPropagation();
    const emoji = event.detail.unicode;

    setEditorState((prev) => {
      try {
        const contentState = prev.getCurrentContent();
        const selectionState = prev.getSelection();

        const newContentState = Modifier.insertText(
          contentState,
          selectionState,
          emoji,
        );

        const newEditorState = EditorState.push(
          prev,
          newContentState,
          "insert-characters",
        );

        return newEditorState;
      } catch (error) {
        console.warn("Error inserting emoji:", error);
        return prev;
      }
    });
  };

  useEffect(() => {
    if (!document.querySelector('script[src*="emoji-picker-element"]')) {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/emoji-picker-element@^1/index.js";
      script.type = "module";
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (!showEmojiPicker || !emojiPickerRef.current) return;

    const emojiPickerElement = emojiPickerRef.current;
    emojiPickerElement.addEventListener("emoji-click", handleEmojiSelect);

    return () => {
      emojiPickerElement.removeEventListener("emoji-click", handleEmojiSelect);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        event.target.id !== "emojiButton"
      ) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    let timeout = null;
    if (isExpanded) {
      timeout = setTimeout(() => {
        setShowFooter(true);
      }, 100);
    } else {
      setShowFooter(false);
    }
    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [isExpanded]);

  const cleanHTML = (html) => {
    try {
      const convertedHtml = convertToHTML(editorState.getCurrentContent());

      let cleaned = preserveBlankLines(preserveExtraSpaces(convertedHtml))
        .replace(/<span[^>]*>(\s|&nbsp;)*<\/span>/g, "")
        .trim();

      if (cleaned && !cleaned.startsWith("<p>")) {
        cleaned = `<p>${cleaned}</p>`;
      }

      return cleaned;
    } catch (error) {
      console.warn("Error cleaning HTML:", error);
      return "";
    }
  };

  const handleSend = () => {
    try {
      const content = editorState.getCurrentContent();
      if (!content.hasText()) {
        toast.error("Enter message");
        return;
      }

      let messageToSend = cleanHTML(content);
      onSendClick(messageToSend);

      if (messageDraftEnabled) {
        clearMessageDraft();
      }

      // Reset editor state and maintain focus
      const newEditorState = EditorState.createEmpty();
      setEditorState(newEditorState);

      // Set a small timeout to ensure state updates before refocusing
      setTimeout(() => {
        if (editorRef.current) {
          if (editorRef.current.focusEditor) {
            editorRef.current.focusEditor();
          } else {
            const editorElement = document.querySelector(".rdw-editor-main");
            if (editorElement) editorElement.focus();
          }
        }
      }, 50);

      if (!isEditing) {
        const newEditorState = EditorState.createEmpty();
        setEditorState(newEditorState);
        setIsExpanded(false);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error sending message");
    }
  };

  // toolbar
  const toggleInlineStyle = (style) => {
    try {
      setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    } catch (error) {
      console.warn("Error toggling inline style:", error);
    }
  };

  const toggleBlockType = (blockType) => {
    try {
      setEditorState(RichUtils.toggleBlockType(editorState, blockType));
    } catch (error) {
      console.warn("Error toggling block type:", error);
    }
  };

  const handleKeyCommand = (command, editorState) => {
    if (command === "send-message") {
      handleSend();
      return "handled";
    }

    try {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        setEditorState(newState);
        return "handled";
      }
    } catch (error) {
      console.warn("Error handling key command:", error);
    }

    return "not-handled";
  };

  // const keyBindingFn = (e) => {
  //   if (e.key === "Backspace") {
  //     try {
  //       const content = editorState.getCurrentContent();
  //       if (!content.hasText() && content.getPlainText().length === 0) {
  //         e.preventDefault();
  //         return null;
  //       }
  //     } catch (error) {
  //       console.warn("Error in backspace handling:", error);
  //       e.preventDefault();
  //       return null;
  //     }
  //   }

  //   if (e.key === "Enter" && !e.shiftKey) {
  //     e.preventDefault();
  //     return "send-message";
  //   }
  //   return null;
  // };

  return (
    <div
      id="messageBox"
      className="message-mt"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#1c2a37",
        borderRadius: "24px",
        transition: "height 0.3s ease-in-out, width 0.3s ease-in-out",
        position: "sticky",
        bottom: "0px",
        width: "100%",
        height: messageBoxHeight,
        border: isEditing ? "2px solid #4CAF50" : "none",
        opacity: isMessageBoxDisabled ? 0.5 : 1,
        pointerEvents: isMessageBoxDisabled ? "none" : "auto",
        cursor: isMessageBoxDisabled ? "not-allowed" : "default",
      }}
    >
      {/* Add a header when in edit mode */}
      {isEditing && (
        <div
          className="d-flex justify-content-between"
          style={{
            padding: "10px 20px",
            backgroundColor: "#2a4052",
            width: "100%",
            borderRadius: "24px 24px 0 0",
            color: "#fff",
          }}
        >
          <span>Editing message...</span>
          {setEditingMessage ? (
            <button
              onClick={() => setEditingMessage(null)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          ) : null}
        </div>
      )}
      {/* EDITOR vs TEXT INPUT */}
      <div
        style={{
          width: "100%",
          padding: "0 20px",
          flexGrow: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        {/* Rich Text Editor */}
        <div
          className="expanded-container"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            position: "relative",
          }}
        >
          {isExpanded ? (
            <CustomToolbar
              editorState={editorState}
              onToggleInlineStyle={toggleInlineStyle}
              onToggleBlockType={toggleBlockType}
            />
          ) : (
            <SimpleToolbar
              handleTextFocus={handleTextFocus}
              showEmojiPicker={showEmojiPicker}
              emojiPickerRef={emojiPickerRef}
              toggleEmojiPicker={toggleEmojiPicker}
            />
          )}
          <hr
            id="horizontal-line"
            style={{
              width: "100%",
              border: "1px solid #2a4052",
              opacity: 1,
              margin: "10px 0",
            }}
          />

          <Editor
            ref={editorRef}
            editorState={editorState}
            onEditorStateChange={handleEditorStateChange}
            wrapperClassName="wrapper-class"
            placeholder="Type your answer...."
            editorClassName="editor-class"
            toolbar={{ options: [] }}
            handleKeyCommand={handleKeyCommand}
            // keyBindingFn={keyBindingFn}
            stripPastedStyles
            readOnly={isMessageBoxDisabled}
            handleReturn={(e) => {
              if (e.shiftKey) {
                return "not-handled";
              }

              if (isExpanded) {
                return "not-handled";
              }

              const hasText = editorState.getCurrentContent().hasText();

              if (hasText) {
                handleSend();
                return "handled";
              }
              toast.error("Enter message");
              return "handled";
            }}
            style={{
              flex: 1,
              minHeight: 0,
            }}
          />
          {showFooter ? (
            <ChatFooter
              handleCloseEditor={handleCloseEditor}
              handleSend={handleSend}
              isEditing={isEditing}
            />
          ) : null}
        </div>
        {!isExpanded ? (
          <button
            id="sendButton"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              marginLeft: "10px",
              alignItems: "center",
              display: "inline-block",
              bottom: "10px",
              right: 0,
            }}
            onClick={handleSend}
          >
            <img src="/icons/telegram.svg" alt="Send" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export { MessageBox };

const EMOJI_PICKER_THEME = {
  "--background": "#121e29",
  "--border-color": "#444",
  "--border-radius": "20px",
  "--input-font-color": "#ffffff",
  "--input-placeholder-color": "rgba(255, 255, 255, 0.55)",
  "--input-border-color": "#444",
  "--button-hover-background": "#484848",
  "--button-active-background": "#555555",
  "--category-font-color": "#efefef",
};

const applyEmojiPickerTheme = (picker) => {
  if (!picker) return false;

  picker.classList.add("dark");
  Object.entries(EMOJI_PICKER_THEME).forEach(([prop, value]) => {
    picker.style.setProperty(prop, value);
  });

  return true;
};

const SimpleToolbar = ({
  toggleEmojiPicker,
  handleTextFocus,
  emojiPickerRef,
  showEmojiPicker,
}) => {
  useEffect(() => {
    let intervalId;

    const tryApplyTheme = () => {
      if (!applyEmojiPickerTheme(emojiPickerRef.current)) return;
      if (intervalId) clearInterval(intervalId);
    };

    tryApplyTheme();
    intervalId = setInterval(tryApplyTheme, 50);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showEmojiPicker, emojiPickerRef]);

  return (
    <div
      id="iconContainer"
      style={{
        paddingTop: "12px",
        display: "flex",
        gap: "20px",
        width: "100%",
        position: "relative",
      }}
    >
      <div style={{ marginLeft: "10px" }}>
        <img
          src="/icons/texticon.svg"
          alt=""
          style={{ cursor: "pointer" }}
          onClick={handleTextFocus}
        />
      </div>

      {/* Emoji Picker */}
      <div
        style={{
          position: "absolute",
          bottom: "35px",
          left: "0px",
          display: showEmojiPicker ? "block" : "none",
          zIndex: 1000,
          width: "350px",
          transform: "scale(0.7)",
          transformOrigin: "bottom left",
        }}
      >
        <emoji-picker ref={emojiPickerRef}></emoji-picker>
      </div>
      <img
        id="emojiButton"
        src="/icons/smileface.svg"
        alt="Emoji"
        style={{
          cursor: "pointer",
          position: "relative",
        }}
        onClick={toggleEmojiPicker}
      />
    </div>
  );
};

const ChatFooter = ({ handleCloseEditor, handleSend, isEditing }) => {
  return (
    <div
      style={{
        marginTop: "10px",
        margin: "20px",
        textAlign: "right",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <button
        onClick={handleCloseEditor}
        className="btn-1-basic btn-1 text-body-b1"
      >
        {isEditing ? "Cancel" : "Close"}
      </button>
      <button
        id="sendExpButton"
        className="btn-1 btn-1-primary text-body-b1"
        onClick={handleSend}
      >
        <svg
          width="17"
          height="14"
          viewBox="0 0 17 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.4752 0.0978383L1.33985 5.79534C0.375167 6.20034 0.380748 6.76284 1.16286 7.01367L4.79198 8.197L13.1887 2.6595C13.5857 2.407 13.9485 2.54284 13.6503 2.8195L6.84731 9.237H6.84571L6.84731 9.23784L6.59697 13.1478C6.96371 13.1478 7.12555 12.972 7.33124 12.7645L9.09398 10.9728L12.7606 13.8037C13.4366 14.1928 13.9222 13.9928 14.0904 13.1495L16.4973 1.29284C16.7437 0.260338 16.1202 -0.207162 15.4752 0.0978383Z"
            fill="white"
          />
        </svg>
        {isEditing ? "Update" : "Send"}
      </button>
    </div>
  );
};
